import React, { useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { arrayMoveImmutable } from "@/utils/Array";
import type { Identifier, XYCoord } from "dnd-core";

interface DragItem {
    index: number;
}

export interface UseDragDropProps {
    index: number;
    onChange?: (from: number, to: number) => void;
    onChangeEnd?: () => void;
    canDrag?(): boolean;
    /**
     * 拖拽方向: 'horizontal' | 'vertical' | 'both'
     * 'both' 时根据移动的主要轴向动态判断
     */
    direction?: "horizontal" | "vertical" | "both";
}

export function useDragDropDataTransformer<T>(dataSource: T[], onDragEnd: (newDataSource: T[]) => void) {
    const [localList, setLocalList] = useState<T[]>(dataSource);

    useEffect(() => {
        setLocalList(dataSource);
    }, [dataSource, dataSource.length]);

    const onLocalChange = (from: number, to: number) => {
        if (from === to) return;

        /** 这里使用回调的方式确保拿到最新的数据源，避免快速拖动时造成错乱 */
        setLocalList((prev) => arrayMoveImmutable(prev, from, to));
    };

    const onLocalDragEnd = () => {
        onDragEnd?.(localList);
    };

    return {
        localList,
        onLocalChange,
        onLocalDragEnd,
    };
}

export default function useDragDrop(props: UseDragDropProps) {
    const { index, onChange, canDrag, onChangeEnd, direction = "horizontal" } = props;

    const ref = useRef<HTMLDivElement>(null);
    const [_, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: "drag-drop-key",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }

            const from = item.index;
            const to = index;
            if (from === to) {
                return;
            }

            const hoverRect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset()!;

            let axis: string = direction;
            if (direction === "both") {
                const midX = (hoverRect.left + hoverRect.right) / 2;
                const midY = (hoverRect.top + hoverRect.bottom) / 2;
                const dx = (clientOffset as XYCoord).x - midX;
                const dy = (clientOffset as XYCoord).y - midY;
                axis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
            }

            let hoverMiddle: number;
            let hoverClient: number;
            if (axis === "horizontal") {
                hoverMiddle = (hoverRect.right - hoverRect.left) / 2;
                hoverClient = (clientOffset as XYCoord).x - hoverRect.left;
            } else {
                hoverMiddle = (hoverRect.bottom - hoverRect.top) / 2;
                hoverClient = (clientOffset as XYCoord).y - hoverRect.top;
            }

            if (from < to && hoverClient < hoverMiddle) {
                return;
            }
            if (from > to && hoverClient > hoverMiddle) {
                return;
            }

            onChange?.(from, to);
            item.index = to;
        },
    });

    const [{ isDragging }, drag] = useDrag(
        {
            type: "drag-drop-key",
            item: () => ({ index }),
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            canDrag,
            end: onChangeEnd,
        },
        [onChangeEnd],
    );

    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));

    return { ref, opacity, isDragging };
}
